import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Badge } from "src/components/base/badge";
import { Button } from "src/components/base/button";
import { Heading, Subheading } from "src/components/base/heading";
import { Input } from "src/components/base/input";
import {
    FolderArrowDownIcon,
    CloudArrowUpIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ChevronRightIcon,
    HomeIcon,
    DocumentIcon,
    ArrowLeftIcon,
} from "@heroicons/react/20/solid";
import { Card } from "src/components/landing/shared";

// Mock-Daten für die Archive
const initialArchives = [
    {
        id: 1,
        name: "External Pentest Report Q1",
        context: "Pentest",
        files: [
            { id: 101, name: "Pentest_Final_V1.pdf", size: "2.4 MB", uploadedAt: "2026-03-10", user: "m.schmidt" },
            {
                id: 102,
                name: "Vulnerability_Scan_Export.csv",
                size: "8.1 MB",
                uploadedAt: "2026-03-11",
                user: "m.schmidt",
            },
            { id: 103, name: "Screenshots_Evidence.zip", size: "1.9 MB", uploadedAt: "2026-03-12", user: "Customer" },
        ],
        expires: "2026-06-20",
        status: "Active",
    },
    {
        id: 2,
        name: "Forensic Images - Case #8821",
        context: "Incident-Response",
        files: [],
        expires: "2026-04-05",
        status: "Expiring soon",
    },
];

export function ArchiveDashboard() {
    const [selectedArchive, setSelectedArchive] = useState<(typeof initialArchives)[0] | null>(null);

    return (
        <div className="space-y-6">
            {/* ── Breadcrumbs ── */}
            <nav className="flex items-center gap-2 text-sm text-zinc-500">
                <HomeIcon className="h-4 w-4" />
                <ChevronRightIcon className="h-3 w-3" />
                <button
                    onClick={() => setSelectedArchive(null)}
                    className="hover:text-zinc-800 dark:hover:text-zinc-200"
                >
                    Platform
                </button>
                <ChevronRightIcon className="h-3 w-3" />
                <button
                    onClick={() => setSelectedArchive(null)}
                    className={`hover:text-zinc-800 dark:hover:text-zinc-200 ${!selectedArchive ? "font-semibold text-zinc-900 dark:text-white" : ""}`}
                >
                    Archive
                </button>
                {selectedArchive && (
                    <>
                        <ChevronRightIcon className="h-3 w-3" />
                        <span className="max-w-[200px] truncate font-semibold text-zinc-900 dark:text-white">
                            {selectedArchive.name}
                        </span>
                    </>
                )}
            </nav>

            {!selectedArchive ? (
                /* ── LIST VIEW ── */
                <>
                    <div className="flex items-end justify-between">
                        <div>
                            <Heading>Customer Data Archive</Heading>
                            <Subheading>Secure bi-directional data exchange with ISMS expiration policy.</Subheading>
                        </div>
                        <Button color="blue">
                            <CloudArrowUpIcon className="mr-2 h-4 w-4" />
                            Create New Archive
                        </Button>
                    </div>

                    <div className="relative mt-8 w-full max-w-sm">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MagnifyingGlassIcon className="h-4 w-4 text-zinc-400" />
                        </div>
                        <Input className="pl-10" placeholder="Search archives..." />
                    </div>

                    <Table className="mt-4" dense>
                        <TableHead>
                            <TableRow>
                                <TableHeader>Name</TableHeader>
                                <TableHeader>Context</TableHeader>
                                <TableHeader>Files</TableHeader>
                                <TableHeader>Expires (ISMS)</TableHeader>
                                <TableHeader className="text-right">Actions</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {initialArchives.map((arc) => (
                                <TableRow
                                    key={arc.id}
                                    className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/5"
                                    onClick={() => setSelectedArchive(arc)}
                                >
                                    <TableCell className="font-semibold text-zinc-900 dark:text-white">
                                        {arc.name}
                                    </TableCell>
                                    <TableCell>
                                        <Badge color={arc.context === "Pentest" ? "purple" : "red"}>
                                            {arc.context}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-zinc-500">{arc.files.length} Files</TableCell>
                                    <TableCell
                                        className={arc.status === "Expiring soon" ? "font-bold text-red-600" : ""}
                                    >
                                        {arc.expires}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button className="p-1 text-zinc-400 hover:text-blue-600">
                                                <FolderArrowDownIcon className="h-5 w-5" />
                                            </button>
                                            <button className="p-1 text-zinc-400 hover:text-red-600">
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            ) : (
                /* ── DETAIL VIEW ── */
                <div className="animate-in fade-in slide-in-from-left-4 space-y-6 duration-300">
                    <div className="flex items-start justify-between">
                        <div className="space-y-1">
                            <button
                                onClick={() => setSelectedArchive(null)}
                                className="mb-2 flex items-center text-xs font-bold uppercase tracking-wider text-blue-600 hover:text-blue-500"
                            >
                                <ArrowLeftIcon className="mr-1 h-3 w-3" /> Back to Archive
                            </button>
                            <Heading>{selectedArchive.name}</Heading>
                            <div className="flex items-center gap-3">
                                <Badge color="zinc">{selectedArchive.context}</Badge>
                                <span className="text-sm text-zinc-500">
                                    ISMS Expiration:{" "}
                                    <span className="font-medium text-zinc-900 dark:text-zinc-100">
                                        {selectedArchive.expires}
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button outline onClick={() => setSelectedArchive(null)}>
                                Close
                            </Button>
                            <Button color="blue">
                                <CloudArrowUpIcon className="mr-2 h-4 w-4" />
                                Upload Files
                            </Button>
                        </div>
                    </div>

                    <Card className="overflow-hidden p-0">
                        <Table dense>
                            <TableHead className="bg-zinc-50 dark:bg-zinc-900/50">
                                <TableRow>
                                    <TableHeader>File Name</TableHeader>
                                    <TableHeader>Size</TableHeader>
                                    <TableHeader>Upload Date</TableHeader>
                                    <TableHeader>Uploaded By</TableHeader>
                                    <TableHeader className="text-right">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedArchive.files.length > 0 ? (
                                    selectedArchive.files.map((file) => (
                                        <TableRow key={file.id}>
                                            <TableCell className="flex items-center gap-2 font-medium">
                                                <DocumentIcon className="h-4 w-4 text-zinc-400" />
                                                {file.name}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-zinc-500">
                                                {file.size}
                                            </TableCell>
                                            <TableCell className="text-zinc-500">{file.uploadedAt}</TableCell>
                                            <TableCell>
                                                <Badge color={file.user === "Customer" ? "emerald" : "zinc"}>
                                                    {file.user}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button className="text-sm font-semibold text-blue-600 hover:underline">
                                                        Download
                                                    </button>
                                                    <button className="text-red-600 hover:text-red-500">
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} className="py-12 text-center italic text-zinc-500">
                                            No files uploaded yet. Drag & drop files or use the upload button.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </div>
            )}
        </div>
    );
}
